import React, { useState, useEffect } from 'react'
import { 
  UserIcon, 
  AcademicCapIcon, 
  StarIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  DocumentTextIcon,
  LightBulbIcon,
  CpuChipIcon,
  EyeIcon,
  PencilIcon,
  ClipboardDocumentListIcon,
  PlusIcon
} from '@heroicons/react/24/outline'
import { motion, AnimatePresence } from 'framer-motion'

interface AdmissionApplication {
  id: string
  studentName: string
  email: string
  phone: string
  age: number
  education: {
    level: string
    school: string
    major: string
    gpa: number
    graduationYear: number
  }
  background: {
    workExperience: string
    skills: string[]
    certifications: string[]
    projects: string[]
  }
  motivation: {
    goals: string
    reasons: string
    expectations: string
  }
  financial: {
    budget: number
    paymentMethod: string
  }
  personalInfo: {
    hobbies: string[]
    personality: string
    learningStyle: string
  }
  submittedAt: Date
  status: 'pending' | 'analyzing' | 'reviewed' | 'approved' | 'rejected'
  aiAnalysis?: AIAnalysisResult
  adminDecision?: AdminDecision
}

interface AIAnalysisResult {
  overallScore: number
  strengths: string[]
  concerns: string[]
  recommendation: 'strongly_recommend' | 'recommend' | 'conditional' | 'not_recommend'
  reasoning: string
  riskFactors: string[]
  successPredictors: string[]
  suggestions: string[]
  detailedAnalysis: {
    academicPotential: number
    motivation: number
    financialStability: number
    culturalFit: number
    successProbability: number
  }
  generatedAt: Date
}

interface AdminDecision {
  decision: 'approved' | 'rejected' | 'pending_interview'
  reasoning: string
  conditions?: string[]
  adminId: string
  decidedAt: Date
}

const AIAdmissionOfficer: React.FC = () => {
  const [applications, setApplications] = useState<AdmissionApplication[]>([])
  const [selectedApplication, setSelectedApplication] = useState<AdmissionApplication | null>(null)
  const [isAnalyzing, setIsAnalyzing] = useState(false)
  const [showDecisionModal, setShowDecisionModal] = useState(false)
  const [filter, setFilter] = useState<'all' | 'pending' | 'analyzed' | 'decided'>('all')
  
  // 添加决策相关状态
  const [decisionForm, setDecisionForm] = useState({
    decision: 'approved' as 'approved' | 'rejected' | 'pending_interview',
    reasoning: '',
    conditions: [] as string[]
  })
  const [isSubmittingDecision, setIsSubmittingDecision] = useState(false)

  // 本地存储key
  const STORAGE_KEY = 'ai_admission_applications'

  // 从本地存储加载数据
  const loadFromStorage = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const parsedData = JSON.parse(stored)
        // 转换日期字符串回Date对象
        return parsedData.map((app: any) => ({
          ...app,
          submittedAt: new Date(app.submittedAt),
          aiAnalysis: app.aiAnalysis ? {
            ...app.aiAnalysis,
            generatedAt: new Date(app.aiAnalysis.generatedAt)
          } : undefined,
          adminDecision: app.adminDecision ? {
            ...app.adminDecision,
            decidedAt: new Date(app.adminDecision.decidedAt)
          } : undefined
        }))
      }
    } catch (error) {
      console.error('加载存储数据失败:', error)
    }
    return null
  }

  // 保存到本地存储
  const saveToStorage = (apps: AdmissionApplication[]) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(apps))
    } catch (error) {
      console.error('保存数据到存储失败:', error)
    }
  }

  // 从 API 或本地存储加载数据
  useEffect(() => {
    fetchApplications()
  }, [])

  const fetchApplications = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) {
        // 无 token 时从本地存储加载
        const storedApplications = loadFromStorage()
        if (storedApplications && storedApplications.length > 0) {
          setApplications(storedApplications)
        }
        return
      }

      // 尝试从 API 获取真实数据
      const response = await fetch('/api/admin/admission-applications', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        const applications = Array.isArray(data) ? data : (data.applications || [])
        if (applications.length > 0) {
          // API 返回了真实数据
          const formattedApps = applications.map((app: any) => ({
            ...app,
            submittedAt: new Date(app.submittedAt || app.createdAt),
            aiAnalysis: app.aiAnalysis ? {
              ...app.aiAnalysis,
              generatedAt: new Date(app.aiAnalysis.generatedAt)
            } : undefined,
            adminDecision: app.adminDecision ? {
              ...app.adminDecision,
              decidedAt: new Date(app.adminDecision.decidedAt)
            } : undefined
          }))
          setApplications(formattedApps)
          saveToStorage(formattedApps)
          return
        }
      }

      // API 未返回数据或失败，尝试从本地存储加载
      const storedApplications = loadFromStorage()
      if (storedApplications && storedApplications.length > 0) {
        setApplications(storedApplications)
      } else {
        // 本地存储也没有数据，显示空状态
        setApplications([])
      }
    } catch (error) {
      console.error('获取申请数据失败:', error)
      // 出错时从本地存储加载
      const storedApplications = loadFromStorage()
      if (storedApplications && storedApplications.length > 0) {
        setApplications(storedApplications)
      }
    }
  }

  // 以下是示例数据的定义（仅用于演示，不会自动加载）
  const sampleApplications: AdmissionApplication[] = [
        {
          id: '1',
          studentName: '张小明',
          email: 'zhang.xiaoming@email.com',
          phone: '13800138001',
          age: 22,
          education: {
            level: '本科',
            school: '清华大学',
            major: '计算机科学',
            gpa: 3.8,
            graduationYear: 2023
          },
          background: {
            workExperience: '在腾讯实习6个月，负责前端开发',
            skills: ['JavaScript', 'React', 'Python', 'Machine Learning'],
            certifications: ['CET-6', 'AWS云计算认证'],
            projects: ['电商平台前端开发', '智能推荐系统', '数据可视化平台']
          },
          motivation: {
            goals: '希望成为全栈工程师，在AI领域深入发展',
            reasons: '对人工智能技术非常感兴趣，希望通过系统学习提升技能',
            expectations: '期望通过1年的学习，能够独立开发AI应用'
          },
          financial: {
            budget: 50000,
            paymentMethod: '分期付款'
          },
          personalInfo: {
            hobbies: ['编程', '阅读', '游泳'],
            personality: '性格开朗，善于沟通，学习能力强',
            learningStyle: '理论结合实践，喜欢项目驱动学习'
          },
          submittedAt: new Date('2024-01-15'),
          status: 'pending'
        },
        {
          id: '2',
          studentName: '李美丽',
          email: 'li.meili@email.com',
          phone: '13900139002',
          age: 25,
          education: {
            level: '硕士',
            school: '北京大学',
            major: '数学',
            gpa: 3.9,
            graduationYear: 2022
          },
          background: {
            workExperience: '在银行工作2年，数据分析师',
            skills: ['Python', 'R', 'SQL', 'Statistics', 'Machine Learning'],
            certifications: ['FRM金融风险管理师', 'CFA一级'],
            projects: ['风控模型开发', '客户行为分析', '投资组合优化']
          },
          motivation: {
            goals: '转向AI领域，成为数据科学家',
            reasons: '数学背景强，希望将理论知识应用到AI实践中',
            expectations: '掌握深度学习和大数据技术'
          },
          financial: {
            budget: 80000,
            paymentMethod: '一次性付款'
          },
          personalInfo: {
            hobbies: ['数学研究', '钢琴', '旅行'],
            personality: '逻辑思维强，注重细节，有研究精神',
            learningStyle: '理论导向，喜欢深入研究原理'
          },
          submittedAt: new Date('2024-01-14'),
          status: 'reviewed',
          aiAnalysis: {
            overallScore: 92,
            strengths: [
              '数学基础极其扎实，硕士学历加分',
              '工作经验丰富，有实际项目经验',
              '学习动机明确，目标清晰',
              '财务状况良好，能够全额支付',
              '性格特点非常适合AI领域'
            ],
            concerns: [
              '年龄稍大，可能学习新技术有一定挑战',
              '从金融转向AI，需要适应期'
            ],
            recommendation: 'strongly_recommend',
            reasoning: '该申请者具备优秀的数学基础和实际工作经验，学习动机明确，财务状况良好。数学背景与AI领域高度匹配，成功概率很高。建议优先录取。',
            riskFactors: [
              '跨行业转换可能需要适应期',
              '理论导向学习风格需要平衡实践'
            ],
            successPredictors: [
              '强大的数学基础',
              '丰富的数据分析经验',
              '明确的职业规划',
              '良好的经济条件'
            ],
            suggestions: [
              '安排额外的编程基础课程',
              '提供更多AI实战项目',
              '匹配有金融AI经验的导师'
            ],
            detailedAnalysis: {
              academicPotential: 95,
              motivation: 88,
              financialStability: 95,
              culturalFit: 90,
              successProbability: 92
            },
            generatedAt: new Date('2024-01-14T10:30:00')
          }
        },
        {
          id: '3',
          studentName: '王强',
          email: 'wang.qiang@email.com',
          phone: '13700137003',
          age: 28,
          education: {
            level: '本科',
            school: '上海交通大学',
            major: '软件工程',
            gpa: 3.6,
            graduationYear: 2020
          },
          background: {
            workExperience: '在阿里巴巴工作3年，后端开发工程师',
            skills: ['Java', 'Python', 'Spring Boot', 'MySQL', 'Redis'],
            certifications: ['Oracle认证', '阿里云架构师'],
            projects: ['电商系统架构', '微服务改造', '分布式缓存系统']
          },
          motivation: {
            goals: '深入学习AI和机器学习，向AI工程师转型',
            reasons: '对AI技术在电商领域的应用很感兴趣',
            expectations: '掌握深度学习框架，能够开发智能推荐系统'
          },
          financial: {
            budget: 45000,
            paymentMethod: '分期付款'
          },
          personalInfo: {
            hobbies: ['编程', '篮球', '摄影'],
            personality: '务实进取，团队合作能力强',
            learningStyle: '实践导向，喜欢动手项目'
          },
          submittedAt: new Date('2024-01-13'),
          status: 'approved',
          aiAnalysis: {
            overallScore: 85,
            strengths: [
              '有丰富的后端开发经验',
              '技术基础扎实，学习能力强',
              '在大厂工作经验，适应能力强',
              '学习目标明确，与工作经验匹配'
            ],
            concerns: [
              'AI基础相对薄弱，需要补强数学基础',
              '预算中等，需要合理安排学习计划'
            ],
            recommendation: 'recommend',
            reasoning: '申请者有扎实的工程背景和实际项目经验，学习动机明确。虽然AI基础稍弱，但工程能力强，转型成功概率较高。',
            riskFactors: [
              '数学基础需要加强',
              '从工程转向算法需要适应'
            ],
            successPredictors: [
              '丰富的工程经验',
              '大厂工作背景',
              '实践导向学习风格',
              '明确的转型目标'
            ],
            suggestions: [
              '补充数学和统计基础课程',
              '重点关注机器学习工程实践',
              '提供推荐系统相关项目'
            ],
            detailedAnalysis: {
              academicPotential: 80,
              motivation: 90,
              financialStability: 75,
              culturalFit: 85,
              successProbability: 85
            },
            generatedAt: new Date('2024-01-13T14:20:00')
          },
          adminDecision: {
            decision: 'approved',
            reasoning: '申请者有丰富的后端开发经验和大厂工作背景，学习动机明确，AI分析评分良好。虽然数学基础稍弱，但工程能力强，相信通过系统学习能够成功转型。同意录取。',
            conditions: [
              '需要先完成数学基础强化课程',
              '安排专门的机器学习导师指导',
              '第一学期重点关注理论基础'
            ],
            adminId: 'admin-001',
            decidedAt: new Date('2024-01-13T16:45:00')
          }
        },
        {
          id: '4',
          studentName: '陈雨',
          email: 'chen.yu@email.com',
          phone: '13600136004',
          age: 24,
          education: {
            level: '本科',
            school: '华中科技大学',
            major: '计算机科学',
            gpa: 3.2,
            graduationYear: 2023
          },
          background: {
            workExperience: '刚毕业，在初创公司实习6个月',
            skills: ['JavaScript', 'Vue.js', 'Node.js', 'MongoDB'],
            certifications: ['CET-4'],
            projects: ['个人博客系统', '小程序开发', '前端组件库']
          },
          motivation: {
            goals: '学习AI技术，希望从事AI相关工作',
            reasons: '对人工智能很感兴趣，想要跟上技术发展趋势',
            expectations: '能够找到AI相关的工作机会'
          },
          financial: {
            budget: 15000,
            paymentMethod: '分期付款'
          },
          personalInfo: {
            hobbies: ['游戏', '动漫', '音乐'],
            personality: '内向但好学，有创新思维',
            learningStyle: '视频学习，需要较多指导'
          },
          submittedAt: new Date('2024-01-12'),
          status: 'rejected',
          aiAnalysis: {
            overallScore: 65,
            strengths: [
              '年轻有潜力，学习时间充裕',
              '有一定的编程基础',
              '对新技术有兴趣和热情'
            ],
            concerns: [
              '学术成绩一般，基础可能不够扎实',
              '工作经验较少，缺乏项目实践',
              '预算有限，可能影响学习连续性',
              '学习动机相对模糊，目标不够明确'
            ],
            recommendation: 'not_recommend',
            reasoning: '申请者虽然年轻有潜力，但基础相对薄弱，工作经验不足，预算也比较有限。学习动机不够明确，成功概率较低。建议先积累更多实践经验后再申请。',
            riskFactors: [
              '基础薄弱影响学习效果',
              '预算不足可能中途退学',
              '缺乏明确职业规划',
              '学习依赖性较强'
            ],
            successPredictors: [
              '年龄优势',
              '学习热情',
              '有一定编程基础'
            ],
            suggestions: [
              '建议先工作1-2年积累经验',
              '提升数学和算法基础',
              '明确职业发展目标',
              '增加预算准备'
            ],
            detailedAnalysis: {
              academicPotential: 60,
              motivation: 70,
              financialStability: 45,
              culturalFit: 65,
              successProbability: 55
            },
            generatedAt: new Date('2024-01-12T11:15:00')
          },
          adminDecision: {
            decision: 'rejected',
            reasoning: '经过AI分析和综合评估，申请者虽然有学习热情，但基础相对薄弱，工作经验不足，预算也比较有限。当前阶段不太适合我们的高强度AI课程。建议申请者先积累1-2年工作经验，提升基础技能后再申请。',
            adminId: 'admin-001',
            decidedAt: new Date('2024-01-12T15:30:00')
          }
        }
      ]
  // 示例数据定义结束 - 此数据不会自动加载，仅用于参考

  // 监听applications变化并保存到本地存储
  useEffect(() => {
    if (applications.length > 0) {
      saveToStorage(applications)
    }
  }, [applications])

  const generateAIAnalysis = async (application: AdmissionApplication) => {
    setIsAnalyzing(true)
    
    try {
      const token = localStorage.getItem('token')
      
      // 尝试调用真实的 AI 分析 API
      if (token) {
        const response = await fetch(`/api/admin/admission-applications/${application.id}/analyze`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(application)
        })

        if (response.ok) {
          const result = await response.json()
          if (result.analysis) {
            const apiAnalysis: AIAnalysisResult = {
              ...result.analysis,
              generatedAt: new Date(result.analysis.generatedAt || new Date())
            }
            
            setApplications(prev => prev.map(app => 
              app.id === application.id 
                ? { ...app, status: 'reviewed', aiAnalysis: apiAnalysis }
                : app
            ))
            
            setSelectedApplication(prev => 
              prev?.id === application.id 
                ? { ...prev, status: 'reviewed', aiAnalysis: apiAnalysis }
                : prev
            )
            
            setIsAnalyzing(false)
            return
          }
        }
      }
      
      // API 不可用时使用本地分析算法
      const analysis: AIAnalysisResult = {
        overallScore: calculateOverallScore(application),
        strengths: generateStrengths(application),
        concerns: generateConcerns(application),
        recommendation: generateRecommendation(application),
        reasoning: generateReasoning(application),
        riskFactors: generateRiskFactors(application),
        successPredictors: generateSuccessPredictors(application),
        suggestions: generateSuggestions(application),
        detailedAnalysis: {
          academicPotential: calculateAcademicScore(application),
          motivation: calculateMotivationScore(application),
          financialStability: application.financial.budget > 30000 ? 90 : 60,
          culturalFit: calculateCulturalFitScore(application),
          successProbability: calculateSuccessScore(application)
        },
        generatedAt: new Date()
      }
      
      // 更新应用状态
      setApplications(prev => prev.map(app => 
        app.id === application.id 
          ? { ...app, status: 'reviewed', aiAnalysis: analysis }
          : app
      ))
      
      setSelectedApplication(prev => 
        prev?.id === application.id 
          ? { ...prev, status: 'reviewed', aiAnalysis: analysis }
          : prev
      )
    } catch (error) {
      console.error('AI 分析失败:', error)
    } finally {
      setIsAnalyzing(false)
    }
  }

  // 本地评分计算函数
  const calculateOverallScore = (app: AdmissionApplication): number => {
    let score = 70
    if (app.education.gpa > 3.5) score += 10
    if (app.education.level === '硕士') score += 8
    if (app.background.workExperience) score += 6
    if (app.background.skills.length > 3) score += 4
    if (app.financial.budget > 40000) score += 2
    return Math.min(score, 100)
  }

  const calculateAcademicScore = (app: AdmissionApplication): number => {
    let score = 60
    if (app.education.gpa >= 3.8) score = 95
    else if (app.education.gpa >= 3.5) score = 85
    else if (app.education.gpa >= 3.0) score = 75
    if (app.education.level === '硕士') score = Math.min(score + 10, 100)
    return score
  }

  const calculateMotivationScore = (app: AdmissionApplication): number => {
    let score = 70
    if (app.motivation.goals.length > 50) score += 10
    if (app.motivation.reasons.length > 50) score += 10
    if (app.motivation.goals.includes('AI') || app.motivation.goals.includes('人工智能')) score += 10
    return Math.min(score, 100)
  }

  const calculateCulturalFitScore = (app: AdmissionApplication): number => {
    let score = 70
    if (app.personalInfo.hobbies.length > 2) score += 10
    if (app.personalInfo.learningStyle) score += 10
    if (app.personalInfo.personality) score += 10
    return Math.min(score, 100)
  }

  const calculateSuccessScore = (app: AdmissionApplication): number => {
    const academic = calculateAcademicScore(app)
    const motivation = calculateMotivationScore(app)
    const financial = app.financial.budget > 30000 ? 90 : 60
    return Math.round((academic + motivation + financial) / 3)
  }

  // AI分析生成函数
  const generateStrengths = (app: AdmissionApplication): string[] => {
    const strengths = []
    if (app.education.gpa > 3.5) strengths.push(`优秀的学术成绩 (GPA: ${app.education.gpa})`)
    if (app.education.level === '硕士') strengths.push('高学历背景，学习能力强')
    if (app.background.workExperience) strengths.push('有相关工作经验，理论结合实践')
    if (app.background.skills.length > 3) strengths.push('技能储备丰富，基础扎实')
    if (app.financial.budget > 40000) strengths.push('财务状况良好，能够支持学习')
    if (app.motivation.goals.includes('AI') || app.motivation.goals.includes('人工智能')) {
      strengths.push('学习目标明确，与AI领域高度匹配')
    }
    return strengths
  }

  const generateConcerns = (app: AdmissionApplication): string[] => {
    const concerns = []
    if (app.age > 30) concerns.push('年龄较大，学习新技术可能有挑战')
    if (app.education.gpa < 3.0) concerns.push('学术成绩一般，需要额外关注')
    if (app.financial.budget < 20000) concerns.push('预算有限，可能影响学习计划')
    if (!app.background.workExperience) concerns.push('缺乏工作经验，需要更多实践指导')
    return concerns
  }

  const generateRecommendation = (app: AdmissionApplication): 'strongly_recommend' | 'recommend' | 'conditional' | 'not_recommend' => {
    let score = 0
    if (app.education.gpa > 3.5) score += 25
    if (app.background.workExperience) score += 20
    if (app.financial.budget > 30000) score += 20
    if (app.background.skills.length > 3) score += 15
    if (app.age < 28) score += 10
    if (app.motivation.goals.includes('AI')) score += 10
    
    if (score >= 85) return 'strongly_recommend'
    if (score >= 70) return 'recommend'
    if (score >= 50) return 'conditional'
    return 'not_recommend'
  }

  const generateReasoning = (app: AdmissionApplication): string => {
    const gpaComment = app.education.gpa > 3.5 ? '学术成绩优秀' : '学术成绩中等'
    const experienceComment = app.background.workExperience ? '有工作经验加分' : '缺乏工作经验'
    const budgetComment = app.financial.budget > 30000 ? '财务状况良好' : '预算有限'
    
    return `基于综合分析：${gpaComment}，${experienceComment}，${budgetComment}。申请者的学习动机和目标与我们的课程高度匹配，建议进一步考虑录取。`
  }

  const generateRiskFactors = (app: AdmissionApplication): string[] => {
    const risks = []
    if (app.age > 25) risks.push('年龄因素可能影响学习适应性')
    if (app.financial.paymentMethod === '分期付款') risks.push('分期付款存在违约风险')
    if (!app.background.projects.length) risks.push('缺乏项目经验，实践能力未知')
    return risks
  }

  const generateSuccessPredictors = (app: AdmissionApplication): string[] => {
    const predictors = []
    if (app.education.gpa > 3.5) predictors.push('优秀的学习能力')
    if (app.background.workExperience) predictors.push('实际工作经验')
    if (app.motivation.goals.length > 50) predictors.push('明确的学习目标')
    if (app.personalInfo.learningStyle.includes('实践')) predictors.push('适合的学习风格')
    return predictors
  }

  const generateSuggestions = (app: AdmissionApplication): string[] => {
    const suggestions = []
    if (app.age > 25) suggestions.push('提供额外的学习支持和指导')
    if (!app.background.workExperience) suggestions.push('增加实践项目和实习机会')
    if (app.financial.budget < 30000) suggestions.push('考虑提供奖学金或优惠政策')
    suggestions.push('定期跟踪学习进度和反馈')
    return suggestions
  }

  const filteredApplications = applications.filter(app => {
    if (filter === 'all') return true
    if (filter === 'pending') return app.status === 'pending'
    if (filter === 'analyzed') return app.aiAnalysis && !app.adminDecision
    if (filter === 'decided') return app.adminDecision
    return true
  })

  const getRecommendationColor = (recommendation: string) => {
    switch (recommendation) {
      case 'strongly_recommend': return 'text-green-600 bg-green-50'
      case 'recommend': return 'text-blue-600 bg-blue-50'
      case 'conditional': return 'text-yellow-600 bg-yellow-50'
      case 'not_recommend': return 'text-red-600 bg-red-50'
      default: return 'text-gray-600 bg-gray-50'
    }
  }

  const getRecommendationText = (recommendation: string) => {
    switch (recommendation) {
      case 'strongly_recommend': return '强烈推荐'
      case 'recommend': return '推荐录取'
      case 'conditional': return '条件录取'
      case 'not_recommend': return '不推荐'
      default: return '未分析'
    }
  }

  // 添加决策处理函数
  const handleDecisionSubmit = async () => {
    if (!selectedApplication || !decisionForm.reasoning.trim()) {
      alert('请填写决策理由')
      return
    }

    setIsSubmittingDecision(true)
    
    // 模拟提交决策过程
    await new Promise(resolve => setTimeout(resolve, 1000))
    
    // 过滤空的条件
    const validConditions = decisionForm.conditions.filter(condition => condition.trim() !== '')
    
    const adminDecision: AdminDecision = {
      decision: decisionForm.decision,
      reasoning: decisionForm.reasoning,
      conditions: validConditions.length > 0 ? validConditions : undefined,
      adminId: 'admin-001', // 这里应该使用实际的管理员ID
      decidedAt: new Date()
    }

    // 正确映射决策状态
    let newStatus: AdmissionApplication['status']
    switch (decisionForm.decision) {
      case 'approved':
        newStatus = 'approved'
        break
      case 'rejected':
        newStatus = 'rejected'
        break
      case 'pending_interview':
        newStatus = 'reviewed' // 面试待定仍然是已分析状态，但有决策记录
        break
      default:
        newStatus = selectedApplication.status
    }

    // 更新申请状态
    const updatedApplication = {
      ...selectedApplication,
      status: newStatus,
      adminDecision
    }

    // 更新应用列表
    setApplications(prev => prev.map(app => 
      app.id === selectedApplication.id ? updatedApplication : app
    ))

    // 更新选中的申请
    setSelectedApplication(updatedApplication)

    setIsSubmittingDecision(false)
    setShowDecisionModal(false)
    
    // 重置表单
    setDecisionForm({
      decision: 'approved',
      reasoning: '',
      conditions: []
    })

    // 显示成功消息
    const decisionText = {
      'approved': '录取',
      'rejected': '拒绝',
      'pending_interview': '面试待定'
    }
    
    const actionText = selectedApplication.adminDecision ? '修改' : '提交'
    alert(`✅ 决策已${actionText}：${decisionText[decisionForm.decision]}`)
  }

  // 重置决策表单
  const resetDecisionForm = () => {
    setDecisionForm({
      decision: 'approved',
      reasoning: '',
      conditions: []
    })
  }

  // 打开决策模态框时重置表单
  const openDecisionModal = (initialDecision: 'approved' | 'rejected' = 'approved') => {
    setDecisionForm({
      decision: initialDecision,
      reasoning: '',
      conditions: []
    })
    setShowDecisionModal(true)
  }

  // 获取状态显示文本
  const getStatusText = (application: AdmissionApplication) => {
    if (application.adminDecision) {
      switch (application.adminDecision.decision) {
        case 'approved':
          return '已录取'
        case 'rejected':
          return '已拒绝'
        case 'pending_interview':
          return '面试待定'
      }
    }
    
    switch (application.status) {
      case 'pending':
        return '待分析'
      case 'analyzed':
      case 'reviewed':
        return '已分析'
      case 'approved':
        return '已录取'
      case 'rejected':
        return '已拒绝'
      default:
        return '未知状态'
    }
  }

  // 获取状态样式
  const getStatusStyle = (application: AdmissionApplication) => {
    if (application.adminDecision) {
      switch (application.adminDecision.decision) {
        case 'approved':
          return 'bg-green-100 text-green-700'
        case 'rejected':
          return 'bg-red-100 text-red-700'
        case 'pending_interview':
          return 'bg-purple-100 text-purple-700'
      }
    }
    
    switch (application.status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-700'
      case 'analyzed':
      case 'reviewed':
        return 'bg-blue-100 text-blue-700'
      case 'approved':
        return 'bg-green-100 text-green-700'
      case 'rejected':
        return 'bg-red-100 text-red-700'
      default:
        return 'bg-gray-100 text-gray-700'
    }
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <CpuChipIcon className="w-8 h-8 text-blue-600 mr-3" />
            AI招生官
          </h1>
          <p className="text-gray-600 mt-1">智能分析申请者背景，为招生决策提供专业建议</p>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-500">
          <div className="flex items-center">
            <div className="w-3 h-3 bg-green-500 rounded-full mr-2"></div>
            AI分析引擎在线
          </div>
        </div>
      </div>

      {/* 统计卡片 */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <UserIcon className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">{applications.length}</h3>
              <p className="text-sm text-gray-600">总申请数</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <ChartBarIcon className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {applications.filter(app => app.aiAnalysis && !app.adminDecision).length}
              </h3>
              <p className="text-sm text-gray-600">已分析</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <CheckCircleIcon className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {applications.filter(app => app.adminDecision?.decision === 'approved').length}
              </h3>
              <p className="text-sm text-gray-600">已录取</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center">
            <StarIcon className="w-8 h-8 text-yellow-600" />
            <div className="ml-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {applications.filter(app => app.aiAnalysis?.recommendation === 'strongly_recommend').length}
              </h3>
              <p className="text-sm text-gray-600">AI强推</p>
            </div>
          </div>
        </div>
      </div>

      {/* 筛选器 */}
      <div className="bg-white p-4 rounded-lg shadow-sm border">
        <div className="flex space-x-4">
          {[
            { key: 'all', label: '全部申请', count: applications.length },
            { key: 'pending', label: '待分析', count: applications.filter(app => app.status === 'pending').length },
            { key: 'analyzed', label: '已分析', count: applications.filter(app => app.aiAnalysis && !app.adminDecision).length },
            { key: 'decided', label: '已决策', count: applications.filter(app => app.adminDecision).length }
          ].map(item => (
            <button
              key={item.key}
              onClick={() => setFilter(item.key as any)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                filter === item.key
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {item.label} ({item.count})
            </button>
          ))}
        </div>
      </div>

      {/* 申请列表和详情 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 申请列表 */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold text-gray-900">申请列表</h2>
          </div>
          <div className="divide-y">
            {filteredApplications.map(application => (
              <motion.div
                key={application.id}
                className={`p-6 cursor-pointer hover:bg-gray-50 transition-colors ${
                  selectedApplication?.id === application.id ? 'bg-blue-50 border-l-4 border-l-blue-600' : ''
                }`}
                onClick={() => setSelectedApplication(application)}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900">{application.studentName}</h3>
                    <p className="text-sm text-gray-600">{application.education.school} · {application.education.major}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {application.submittedAt.toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex flex-col items-end space-y-2">
                    {application.aiAnalysis && (
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        getRecommendationColor(application.aiAnalysis.recommendation)
                      }`}>
                        {getRecommendationText(application.aiAnalysis.recommendation)}
                      </span>
                    )}
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      getStatusStyle(application)
                    }`}>
                      {getStatusText(application)}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* 申请详情和AI分析 */}
        <div className="space-y-6">
          {selectedApplication ? (
            <>
              {/* 基本信息 */}
              <div className="bg-white rounded-lg shadow-sm border p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">申请者信息</h3>
                  {selectedApplication.status === 'pending' && (
                    <button
                      onClick={() => generateAIAnalysis(selectedApplication)}
                      disabled={isAnalyzing}
                      className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      <CpuChipIcon className="w-4 h-4 mr-2" />
                      {isAnalyzing ? '分析中...' : 'AI分析'}
                    </button>
                  )}
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">姓名</label>
                      <p className="text-gray-900">{selectedApplication.studentName}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">年龄</label>
                      <p className="text-gray-900">{selectedApplication.age}岁</p>
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">教育背景</label>
                    <p className="text-gray-900">
                      {selectedApplication.education.school} · {selectedApplication.education.major}
                    </p>
                    <p className="text-sm text-gray-600">
                      GPA: {selectedApplication.education.gpa} · 毕业年份: {selectedApplication.education.graduationYear}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">技能</label>
                    <div className="flex flex-wrap gap-2 mt-1">
                      {selectedApplication.background.skills.map(skill => (
                        <span key={skill} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">学习目标</label>
                    <p className="text-gray-900">{selectedApplication.motivation.goals}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-gray-500">预算</label>
                    <p className="text-gray-900">¥{selectedApplication.financial.budget.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              {/* AI分析结果 */}
              {selectedApplication.aiAnalysis && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center mb-4">
                    <LightBulbIcon className="w-6 h-6 text-yellow-500 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">AI分析报告</h3>
                    <span className="ml-auto text-sm text-gray-500">
                      {selectedApplication.aiAnalysis.generatedAt.toLocaleString()}
                    </span>
                  </div>
                  
                  {/* 总分和推荐 */}
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg">
                      <div className="text-3xl font-bold text-blue-600">
                        {selectedApplication.aiAnalysis.overallScore}
                      </div>
                      <div className="text-sm text-gray-600">综合评分</div>
                    </div>
                    <div className="text-center p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg">
                      <div className={`text-lg font-semibold ${
                        selectedApplication.aiAnalysis.recommendation === 'strongly_recommend' ? 'text-green-600' :
                        selectedApplication.aiAnalysis.recommendation === 'recommend' ? 'text-blue-600' :
                        selectedApplication.aiAnalysis.recommendation === 'conditional' ? 'text-yellow-600' :
                        'text-red-600'
                      }`}>
                        {getRecommendationText(selectedApplication.aiAnalysis.recommendation)}
                      </div>
                      <div className="text-sm text-gray-600">AI建议</div>
                    </div>
                  </div>
                  
                  {/* 详细分析 */}
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">优势</h4>
                      <ul className="space-y-1">
                        {selectedApplication.aiAnalysis.strengths.map((strength, index) => (
                          <li key={index} className="flex items-start">
                            <CheckCircleIcon className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                            <span className="text-sm text-gray-700">{strength}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    {selectedApplication.aiAnalysis.concerns.length > 0 && (
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2">关注点</h4>
                        <ul className="space-y-1">
                          {selectedApplication.aiAnalysis.concerns.map((concern, index) => (
                            <li key={index} className="flex items-start">
                              <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500 mr-2 mt-0.5 flex-shrink-0" />
                              <span className="text-sm text-gray-700">{concern}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">AI推理</h4>
                      <p className="text-sm text-gray-700 bg-gray-50 p-3 rounded-lg">
                        {selectedApplication.aiAnalysis.reasoning}
                      </p>
                    </div>
                    
                    {/* 维度分析 */}
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-3">各维度评分</h4>
                      <div className="space-y-2">
                        {Object.entries(selectedApplication.aiAnalysis.detailedAnalysis).map(([key, value]) => {
                          const labels = {
                            academicPotential: '学术潜力',
                            motivation: '学习动机',
                            financialStability: '财务稳定性',
                            culturalFit: '文化匹配度',
                            successProbability: '成功概率'
                          }
                          return (
                            <div key={key} className="flex items-center">
                              <div className="w-20 text-sm text-gray-600">{labels[key as keyof typeof labels]}</div>
                              <div className="flex-1 mx-3">
                                <div className="bg-gray-200 rounded-full h-2">
                                  <div 
                                    className="bg-blue-600 h-2 rounded-full transition-all duration-1000"
                                    style={{ width: `${value}%` }}
                                  ></div>
                                </div>
                              </div>
                              <div className="w-12 text-sm font-medium text-gray-900">{value}</div>
                            </div>
                          )
                        })}
                      </div>
                    </div>
                  </div>
                  
                  {/* 操作按钮 */}
                  {!selectedApplication.adminDecision ? (
                    <div className="flex space-x-3 mt-6">
                      <button
                        onClick={() => openDecisionModal()}
                        className="flex-1 bg-green-600 text-white py-2 px-4 rounded-lg hover:bg-green-700 transition-colors"
                      >
                        <CheckCircleIcon className="w-4 h-4 inline mr-2" />
                        录取决定
                      </button>
                      <button
                        onClick={() => openDecisionModal('rejected')}
                        className="flex-1 bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 transition-colors"
                      >
                        <XCircleIcon className="w-4 h-4 inline mr-2" />
                        拒绝申请
                      </button>
                    </div>
                  ) : null}
                </div>
              )}

              {/* 管理员决策历史 */}
              {selectedApplication.adminDecision && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center mb-4">
                    <ClipboardDocumentListIcon className="w-6 h-6 text-purple-500 mr-2" />
                    <h3 className="text-lg font-semibold text-gray-900">管理员决策</h3>
                    <span className="ml-auto text-sm text-gray-500">
                      {selectedApplication.adminDecision.decidedAt.toLocaleString()}
                    </span>
                  </div>
                  
                  {/* 决策结果 */}
                  <div className="mb-4">
                    <div className="flex items-center space-x-3">
                      <span className="text-sm font-medium text-gray-600">决策结果：</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedApplication.adminDecision.decision === 'approved' 
                          ? 'bg-green-100 text-green-700' 
                          : selectedApplication.adminDecision.decision === 'rejected'
                          ? 'bg-red-100 text-red-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {selectedApplication.adminDecision.decision === 'approved' 
                          ? '✅ 已录取' 
                          : selectedApplication.adminDecision.decision === 'rejected'
                          ? '❌ 已拒绝'
                          : '📝 面试待定'}
                      </span>
                    </div>
                  </div>
                  
                  {/* 决策理由 */}
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-900 mb-2">决策理由</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-700 leading-relaxed">
                        {selectedApplication.adminDecision.reasoning}
                      </p>
                    </div>
                  </div>
                  
                  {/* 决策条件 */}
                  {selectedApplication.adminDecision.conditions && selectedApplication.adminDecision.conditions.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-gray-900 mb-2">附加条件</h4>
                      <ul className="space-y-2">
                        {selectedApplication.adminDecision.conditions.map((condition, index) => (
                          <li key={index} className="flex items-start">
                            <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                            <span className="text-sm text-gray-700">{condition}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  
                  {/* 决策者信息 */}
                  <div className="flex items-center justify-between text-sm text-gray-500 pt-4 border-t">
                    <span>决策者：管理员 ({selectedApplication.adminDecision.adminId})</span>
                    <span>决策时间：{selectedApplication.adminDecision.decidedAt.toLocaleDateString()}</span>
                  </div>
                  
                  {/* 修改决策按钮 */}
                  <div className="mt-4">
                    <button
                      onClick={() => {
                        setDecisionForm({
                          decision: selectedApplication.adminDecision!.decision,
                          reasoning: selectedApplication.adminDecision!.reasoning,
                          conditions: selectedApplication.adminDecision!.conditions || []
                        })
                        setShowDecisionModal(true)
                      }}
                      className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                    >
                      <PencilIcon className="w-4 h-4 inline mr-2" />
                      修改决策
                    </button>
                  </div>
                </div>
              )}

              {/* 加载动画 */}
              {isAnalyzing && (
                <div className="bg-white rounded-lg shadow-sm border p-6">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">AI正在分析申请者信息...</span>
                  </div>
                  <div className="mt-4 space-y-2">
                    <div className="text-sm text-gray-500 animate-pulse">🔍 分析教育背景...</div>
                    <div className="text-sm text-gray-500 animate-pulse">📊 评估技能匹配度...</div>
                    <div className="text-sm text-gray-500 animate-pulse">💡 生成个性化建议...</div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
              <EyeIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">选择申请者</h3>
              <p className="text-gray-500">从左侧列表中选择一个申请者查看详细信息和AI分析</p>
            </div>
          )}
        </div>
      </div>

      {/* 决策模态框 */}
      <AnimatePresence>
        {showDecisionModal && selectedApplication && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            onClick={() => setShowDecisionModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-lg p-6 w-full max-w-md mx-4"
              onClick={e => e.stopPropagation()}
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">
                {selectedApplication.adminDecision ? '修改' : '新建'}招生决策 - {selectedApplication.studentName}
              </h3>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">决策结果</label>
                  <select 
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value={decisionForm.decision}
                    onChange={(e) => setDecisionForm({ ...decisionForm, decision: e.target.value as 'approved' | 'rejected' | 'pending_interview' })}
                  >
                    <option value="approved">✅ 录取</option>
                    <option value="rejected">❌ 拒绝</option>
                    <option value="pending_interview">📝 面试待定</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    决策理由 <span className="text-red-500">*</span>
                  </label>
                  <textarea 
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 h-24 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="请详细说明决策理由，包括关键考虑因素..."
                    value={decisionForm.reasoning}
                    onChange={(e) => setDecisionForm({ ...decisionForm, reasoning: e.target.value })}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    字数: {decisionForm.reasoning.length}/500
                  </p>
                </div>
                
                {/* 附加条件 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    附加条件 <span className="text-gray-400">(可选)</span>
                  </label>
                  <div className="space-y-2">
                    {decisionForm.conditions.map((condition, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <input
                          type="text"
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          placeholder="输入条件..."
                          value={condition}
                          onChange={(e) => {
                            const newConditions = [...decisionForm.conditions]
                            newConditions[index] = e.target.value
                            setDecisionForm({ ...decisionForm, conditions: newConditions })
                          }}
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const newConditions = decisionForm.conditions.filter((_, i) => i !== index)
                            setDecisionForm({ ...decisionForm, conditions: newConditions })
                          }}
                          className="text-red-500 hover:text-red-700 px-2 py-1"
                        >
                          删除
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setDecisionForm({ 
                          ...decisionForm, 
                          conditions: [...decisionForm.conditions, ''] 
                        })
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                    >
                      <PlusIcon className="w-4 h-4 mr-1" />
                      添加条件
                    </button>
                  </div>
                </div>
                
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowDecisionModal(false)}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleDecisionSubmit}
                    disabled={isSubmittingDecision || !decisionForm.reasoning.trim()}
                    className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isSubmittingDecision ? '提交中...' : '确认决策'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default AIAdmissionOfficer 